/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { observable, action } from "mobx";
import { SWITCH_PREVIEW_STATE, appIdentifiers } from "constants/enums";
import { contextMenuTriggerType } from "dc-preview-utils/src/Constants/enums";
import { contextMenuManager } from "contextMenuManager";
import { isPersonalCommentingEnabled, enableToolsInMultidoc } from "utils/FeatureFlagUtils";
import { isCurrentDeviceMobile, isCurrentDeviceTablet } from "dc-preview-utils/src/Utils/DeviceUtils";
import { isDownloadFeatureEnabled } from "selectors/appSelectors";
import { downloadAssetFromDocApi } from "utils/DownloadUtility";
import { shouldStartRendering, isSinglePageMode } from "selectors/documentSelectors";
import { pageContentHandler } from "pageContentHandler";
import { explicitlyDisableFeatures } from "publicApis/constants";
import { printHandler } from "print/printHandler";
import { isBoolean } from "utils/APIUtils";
import { overlayEvents } from "overlays/OverlayEvents";
import { overlayEventNames } from "constants/constants";

class AppStore {
  @observable isSearchEnabled;
  @observable isReadOnlyModeEnabled;
  @observable isZoomEnabled;
  @observable isDownloadEnabled;
  @observable isPrintEnabled;
  @observable isParentWebsiteScrolling;
  @observable isTextSelectionEnabled;
  @observable selectedTool = "";
  @observable shouldUseModernViewer;
  @observable commentsAdditionalConfig;
  @observable rhpRef;
  @observable currRHPFeature;
  @observable selectedDropinForRHP;
  @observable rhpSkeletonDropinId;
  @observable isPanActive;
  @observable useAFFontPicker;
  @observable usePremiumIndicator;
  @observable showGetAppDialog;
  @observable shouldUseMobileModernViewer;
  @observable showFTE;
  @observable shouldShowInteractiveTooltips;
  @observable enablePDFJavaScriptFeatureFlag;
  @observable suppressPasswordDialog;
  @observable enableOverprintSupport;
  @observable fileLocation;
  @observable isRHPResizing;
  @observable disableEditFeatures;
  @observable showAssistantInRHP;
  @observable enableThemeSwitcher;
  @observable shouldShowAnimation;
  @observable switchPreviewState;
  @observable isMultiDocModeEnabled;

  fallbackPrintTimeout;
  editEnterInfo;
  fontAccessType;
  enableEditForFreeUsers;
  useIframelessPrint;
  useWindowPrint;
  useFallbackPrint;
  jsEnginePromise;
  jsEngine;
  jsSAB;
  // TODO:: move it in JSstore once the jsstores are available in preiew dropin
  isJSScriptExecuting;
  intl;
  overlaysManager;
  linearizationBlockSize;
  shouldEnableEditGenAI;
  fillSignConfig;
  showPremiumIconForNonDCWebClients;
  isCopyWithFormattingEnabled;
  @observable shouldEnableOutline;
  @observable shouldEnableQnA;
  hideStickyToolOnTap;
  enableRHPResizing;
  genAIManager;
  shouldDisableProtectedPDFCancel;
  @observable shouldEnableInlineOrganize;
  @observable shouldUseNewThumbnailsGrid;
  jsEngineInitContextsParams;
  popupConfig;
  imageClickSend;
  @observable shouldShowPopup;

  constructor() {
    this.init();
  }

  initStore(docApi, stores) {
    if (docApi) {
      this.docApi = docApi;
    }
    if (stores) {
      this.stores = stores;
    }
  }

  @action
  init(switchPreview = false) {
    this.intl = null;
    this.isSearchEnabled = true;
    this.isReadOnlyModeEnabled = false;
    this.isZoomEnabled = true;
    this.isDownloadEnabled = true;
    this.isPrintEnabled = true;
    this.isParentWebsiteScrolling = false;
    this.isTextSelectionEnabled = true;
    this.selectedTool = "";
    this.isPanActive = false;
    this.useAFFontPicker = false;
    this.usePremiumIndicator = false;
    this.enablePDFJavaScriptFeatureFlag = true;
    this.enableOverprintSupport = false;
    this.isRHPResizing = false;
    this.disableEditFeatures = {};
    this.imageClickSend = false;
    // non-obserables
    this.windowHeight = 0;
    this.useIframelessPrint = false;
    this.useWindowPrint = false;
    this.useFallbackPrint = false;
    // This array contains the features user want to explicitely disable e.g. zoom, HUD etc
    // It helps to retain this info while dynamically changing view modes using external API
    this.explicitlyDisabledFeatures = [];
    if (!switchPreview) {
      this.shouldUseModernViewer = false;
      this.rhpRef = null;
      this.currRHPFeature = null;
      this.shouldEnableOutline = false;
      this.shouldEnableQnA = false;
      this.switchPreviewState = SWITCH_PREVIEW_STATE.NONE;
      this.enableMultiDocMode(false);
    }
    this.selectedDropinForRHP = "";
    this.rhpSkeletonDropinId = "";
    this.enableLinearization = false;
    this.fontAccessType = undefined;
    this.enableEditForFreeUsers = false;
    this.jsEnginePromise = null;
    this.jsEngine = null;
    this.jsSAB = null;
    this.isJSScriptExecuting = false;
    this.overlaysManager = null;
    this.useViewSDKForAcroformSubmit = false;
    this.editEnterInfo = undefined;
    this.killSwitchJSOverridePromise = Promise.resolve();
    this.supportPDFJavaScriptPromise = Promise.resolve();
    this.supportPDFJavaScript = undefined;
    this.showGetAppDialog = false;
    this.shouldUseMobileModernViewer = false;
    this.customLoader = undefined;
    this.showFTE = {
      cohortId: null
    };
    this.shouldShowInteractiveTooltips = false;
    this.enableAcroformValidationFormatting = true;
    this.suppressPasswordDialog = false;
    this.fallbackPrintTimeout = 5000;
    this.fileLocation = undefined;
    this.shouldEnableEditGenAI = false;
    // "fillSignConfig" config is being passed as an object.
    this.fillSignConfig = {};
    this.showPremiumIconForNonDCWebClients = false;
    this.isCopyWithFormattingEnabled = false;
    this.showAssistantInRHP = false;
    this.enableRHPResizing = false;
    this.enableThemeSwitcher = false;
    this.shouldShowAnimation = false;
    this.genAIViewerController = null;
    this.shouldEnableInlineOrganize = false;
    this.shouldDisableProtectedPDFCancel = false;
    this.shouldUseNewThumbnailsGrid = false;
    this.jsEngineInitContextsParams = {};
    this.popupConfig = {};
    this.shouldShowPopup = false;
  }

  setIntl(intl) {
    this.intl = intl;
  }

  setGenAIViewerController(genAIViewerController) {
    this.genAIViewerController = genAIViewerController;
  }

  @action.bound
  setSwitchPreviewState(value) {
    this.switchPreviewState = value;
  }

  setOverlaysManager(overlaysManager) {
    this.overlaysManager = overlaysManager;
  }

  setEditEnterInfo(editEnterInfo) {
    this.editEnterInfo = editEnterInfo;
  }

  setHideStickyToolOnTap(enable) {
    this.hideStickyToolOnTap = enable;
  }

  @action
  enableInlineOrganize(enable) {
    this.shouldEnableInlineOrganize = enable;
  }

  @action
  disableProtectedPDFCancelButton(enable) {
    this.shouldDisableProtectedPDFCancel = enable;
  }

  /**
   *
   * @param {{ dropinId: string | null }} value
   */
  @action.bound setShowFTE(value) {
    this.showFTE = value;
  }

  /**
   *
   * @param {boolean} value
   */
  @action.bound setShouldShowInteractiveTooltips(value) {
    this.shouldShowInteractiveTooltips = value;
  }

  @action.bound setUseIframelessPrint(value) {
    this.useIframelessPrint = value;
  }

  @action.bound setUseWindowPrint(value) {
    this.useWindowPrint = value;
  }

  @action.bound enableFallbackPrint(enable) {
    this.useFallbackPrint = enable;
  }

  @action.bound
  setSelectedTool(toolName) {
    this.selectedTool = toolName;
    overlayEvents.trigger(overlayEventNames.TOOL_SELECTED, toolName);
  }

  @action.bound
  setPanActive(value) {
    if (this.isPanActive === value) {
      return;
    }
    this.isPanActive = value;
  }

  @action.bound
  setSelectedDropinForRHP(selectedDropin) {
    if (selectedDropin !== undefined) this.selectedDropinForRHP = selectedDropin;
  }

  @action.bound
  setRhpSkeletonDropinId(dropin) {
    if (dropin !== undefined && dropin !== "") this.rhpSkeletonDropinId = dropin;
  }

  @action.bound
  setCurrRHPFeature(selectedDropin) {
    this.currRHPFeature = selectedDropin;
  }

  @action.bound
  setIsResizing(isRHPResizing) {
    this.isRHPResizing = isRHPResizing;
  }

  @action.bound
  setRHPRef(rhpRef) {
    if (rhpRef !== undefined) this.rhpRef = rhpRef;
  }

  get rhpRefValue() {
    return this.rhpRef;
  }

  @action enableTextSelection(value) {
    if (this.isTextSelectionEnabled === value) {
      return;
    }
    this.isTextSelectionEnabled = value;
    if (!this.isTextSelectionEnabled) {
      pageContentHandler.getTextManagerInstance().clearSelection(this.stores);
    }
  }

  @action setSuppressPasswordDialog(value) {
    this.suppressPasswordDialog = value;
  }

  setFallbackPrintTimeout(value) {
    this.fallbackPrintTimeout = value;
  }

  @action
  enableSearchFeature(value) {
    if (this.isSearchEnabled === value) {
      return;
    }
    this.isSearchEnabled = value;
  }

  @action
  enablePrintFeature(value) {
    if (this.isPrintEnabled === value) {
      return;
    }
    this.isPrintEnabled = value;
    if (printHandler.shouldUseBrowserPrint()) {
      this.stores.documentStore.setShouldCreatePrintIframe(value);
    }
    this.stores.hudStore.customizeHudComponent(appIdentifiers.PRINT, { visible: value });
  }

  @action
  enableDownloadFeature(value) {
    if (this.isDownloadEnabled === value) {
      return;
    }
    this.isDownloadEnabled = value;
  }

  @action.bound setEnableThemeSwitcher(value) {
    this.enableThemeSwitcher = value;
  }

  @action
  enableReadOnlyMode(value) {
    this.isReadOnlyModeEnabled = value;
  }

  @action
  enableMultiDocMode(value) {
    if (this.isMultiDocModeEnabled === value) return;
    this.isMultiDocModeEnabled = value;
    enableToolsInMultidoc().then(enableTools => {
      if (!enableTools) overlayEvents.trigger(overlayEventNames.MULTI_DOC_MODE_CHANGED, value);
    });
  }

  @action
  setUseAFFontPicker(value) {
    this.useAFFontPicker = value;
  }

  @action
  setUsePremiumIndicator(value) {
    this.usePremiumIndicator = value;
  }

  @action
  setDisableEditFeatures(value) {
    this.disableEditFeatures = value;
  }

  @action
  enableZoomFeature(value) {
    this.isZoomEnabled = value;
    const showPageViewModifierButtons = value && !isSinglePageMode(this.stores);
    const showZoomControl = isCurrentDeviceMobile(this.stores) || isCurrentDeviceTablet(this.stores) ? false : value;
    this.stores.hudStore.customizeHudComponent(appIdentifiers.ZOOM_CONTROL, { visible: showZoomControl });
    this.stores.hudStore.customizeHudComponent(appIdentifiers.FIT_PAGE, { visible: showPageViewModifierButtons });
    this.stores.hudStore.customizeHudComponent(appIdentifiers.FIT_WIDTH, { visible: showPageViewModifierButtons });
  }

  @action
  setIsParentWebsiteScrolling(value) {
    this.isParentWebsiteScrolling = value;
  }

  @action
  setFileLocation(fileLocation) {
    this.fileLocation = fileLocation;
  }

  initializeRightClickContextMenu(shouldDisableDownloadInContextMenu) {
    if (isPersonalCommentingEnabled(this.stores)) {
      shouldDisableDownloadInContextMenu = true;
    }

    if (!isCurrentDeviceMobile(this.stores)) {
      contextMenuManager.subscribe({
        name: "Download_PDF",
        contextMenuHandler: () => {
          if (!shouldDisableDownloadInContextMenu && isDownloadFeatureEnabled(this.stores)) {
            return Promise.resolve([
              {
                title: "contextMenu.downloadDocument",
                eventName: "saveas",
                eventHandler: stores => {
                  downloadAssetFromDocApi(stores, {
                    triggeredFrom: "contextMenu"
                  });
                },
                args: [this.stores],
                position: 40
              },
              {
                title: "separator",
                position: 40
              }
            ]);
          }
          return Promise.resolve([]);
        },
        type: contextMenuTriggerType.RIGHT_CLICK
      });
    }
  }

  setWindowHeight(value) {
    this.windowHeight = value;
  }

  setExplicitlyDisabledFeature(feature, value) {
    if (explicitlyDisableFeatures.hasOwnProperty(feature)) {
      let index = this.explicitlyDisabledFeatures.indexOf(feature);
      if (value) {
        if (index === -1) this.explicitlyDisabledFeatures.push(feature);
      } else {
        if (index !== -1) this.explicitlyDisabledFeatures.splice(index, 1);
      }
    }
  }

  @action
  setShouldUseModernViewer(shouldUseModernViewer) {
    if (isBoolean(shouldUseModernViewer)) {
      this.shouldUseModernViewer = shouldUseModernViewer;
    }
  }

  @action
  setShouldUseMobileModernViewer(shouldUseMobileModernViewer) {
    if (isBoolean(shouldUseMobileModernViewer)) {
      this.shouldUseMobileModernViewer = shouldUseMobileModernViewer;
    }
  }

  @action
  setCommentsAdditionalConfig(commentsAdditionalConfig) {
    // Need to set wether it's undefined/null or an object, to make sure we don't use an outdated value.
    this.commentsAdditionalConfig = commentsAdditionalConfig;
  }

  setEnableLinearization(enableLinearization) {
    this.enableLinearization = enableLinearization;
  }

  setEnableCNPDF(enableCNPDF) {
    this.enableCNPDF = enableCNPDF;
  }

  setCustomLoader(customLoader) {
    this.customLoader = customLoader;
  }

  setUseViewSDKForAcroformSubmit(useViewSDKForAcroformSubmit) {
    this.useViewSDKForAcroformSubmit = useViewSDKForAcroformSubmit;
  }

  setFontAccessType(fontAccessType) {
    this.fontAccessType = fontAccessType;
  }

  setEnableEditForFreeUsers(enableEditForFreeUsers) {
    this.enableEditForFreeUsers = enableEditForFreeUsers;
  }

  setAcroformValidationFormatting(shouldEnable) {
    this.enableAcroformValidationFormatting = shouldEnable;
  }

  instantiateJSEngine() {
    if (this.jsEnginePromise) {
      return this.jsEnginePromise;
    }

    return (this.jsEnginePromise = import(/* webpackChunkName: "jsEngine-chunk" */ "javascript/jsEngine").then(
      ({ default: JSEngine }) => {
        this.jsEngine = new JSEngine(this.stores);
        return this.jsEngine;
      }
    ));
  }

  setJsSAB(sab) {
    this.jsSAB = sab;
  }

  setIsJSScriptExecuting(value) {
    this.isJSScriptExecuting = value;
  }

  setKillSwitchJSOverridePromise(killSwitchJSOverridePromise) {
    this.killSwitchJSOverridePromise = killSwitchJSOverridePromise;
  }

  setSupportPDFJavaScriptPromise(supportPDFJavaScriptPromise) {
    this.supportPDFJavaScriptPromise = supportPDFJavaScriptPromise;
  }

  setSupportPDFJavaScript(supportPDFJavaScript) {
    this.supportPDFJavaScript = supportPDFJavaScript;
  }

  @action
  setShowGetAppDialog(value) {
    this.showGetAppDialog = value;
  }

  @action.bound
  setEnablePDFJavaScriptFeatureFlag(value) {
    this.enablePDFJavaScriptFeatureFlag = value;
  }

  setLinearizationBlockSize(linearizationBlockSize) {
    this.linearizationBlockSize = linearizationBlockSize;
  }

  @action.bound
  setEnableOverprintSupport(enable) {
    this.enableOverprintSupport = enable;
  }

  setShouldEnableEditGenAI(shouldEnableEditGenAI) {
    this.shouldEnableEditGenAI = shouldEnableEditGenAI;
  }

  setFillSignConfig(fillSignConfig) {
    this.fillSignConfig = fillSignConfig;
  }

  setShowPremiumIconForNonDCWebClients(showPremiumIconForNonDCWebClients) {
    this.showPremiumIconForNonDCWebClients = showPremiumIconForNonDCWebClients;
  }

  enableCopyWithFormatting(value) {
    this.isCopyWithFormattingEnabled = value;
  }

  @action
  setShouldEnableOutline(shouldEnableOutline) {
    this.shouldEnableOutline = shouldEnableOutline;
  }

  @action
  setShouldEnableQnA(shouldEnableQnA) {
    this.shouldEnableQnA = shouldEnableQnA;
  }

  @action
  setShowAssistantInRHP(showAssistantInRHP) {
    this.showAssistantInRHP = showAssistantInRHP;
  }

  setShouldEnableRHPResizing(enableRHPResizing) {
    this.enableRHPResizing = enableRHPResizing;
  }

  @action
  setShouldShowAnimation(shouldShowAnimation) {
    this.shouldShowAnimation = shouldShowAnimation;
  }

  setJSEngineInitContextsParams(params) {
    this.jsEngineInitContextsParams = Object.assign(this.jsEngineInitContextsParams, params);

    if (this.jsEngine) {
      this.jsEngine.initContexts(this.jsEngineInitContextsParams);
    }
  }

  setPopupConfig(args) {
    this.popupConfig = args;
  }

  @action
  setShouldShowPopup(value) {
    this.shouldShowPopup = value;
  }

  @action
  setShouldUseNewThumbnailsGrid(value) {
    this.shouldUseNewThumbnailsGrid = value;
  }

  setImageClickSend(value) {
    this.imageClickSend = value;
  }
}

export { AppStore };
